import { gql, type TypedDocumentNode } from "@apollo/client";
import { PHONE_NUMBER_FRAGMENT, ADDRESS_FRAGMENT } from "@/fragments";
import type { ApplicationOrderSummary } from "./types";
import type { HighestLevelOfEducation } from "../education";
import type { BackgroundCheck, ExperienceCredits, QualifyingLevel } from "@/types";

export interface ApplicationPlayingAbilityTestStatus {
  status?: string | null;
  completed: boolean;
  expired: boolean;
  startDate?: string | null;
  expirationDate?: string | null;
  activeSuspension?: {
    startDate?: string | null;
    endDate?: string | null;
  } | null;
}

export interface ApplicationPgmLevelStatus {
  key: string;
  name: string;
  status: string;
  numeral: number;
  startDate?: string | null;
  expirationDate?: string | null;
}

export interface ApplicationPgmProgressStatus {
  currentLevelKey?: string | null;
  currentLevelCode?: string | null;
  curriculumVersion?: string | null;
  mostRecentCompletedLevelKey?: string | null;
  programExpirationDate?: string | null;
  levels: ApplicationPgmLevelStatus[];
}

export interface ApplicationBeneficiary {
  fullName: string | null;
  relationship: string | null;
  dateOfBirth: string | null;
  ssnLast4: string | null;
  percent: number | null;
}

export interface ApplicationDocument {
  id: string;
  filename: string;
  url: string;
  createdAt: string;
}

export interface ApplicationEducationDetails {
  schoolName: string | null;
  graduationMonth: string | null;
  graduationYear: string | null;
  highestLevelOfEducation: HighestLevelOfEducation | null;
}

export interface ApplicationEmploymentDetails {
  facilityId: string | null;
  employer: string | null;
  sectionName: string | null;
  jobTitle: string | null;
  jobClassification: string | null;
  startDate: string | null;
  endDate: string | null;
  country: string | null;
  workingLocation: "ON_SITE" | "REMOTE" | null;
  supervisorName: string | null;
  jobDescription: string | null;
  affiliationSection: {
    id: string;
    name: string;
  } | null;
}

export interface ApplicationPersonPhoneNumber {
  id: string;
  phoneNumber: string;
  phoneExtension: string | null;
  phoneType: string;
  phoneCountry: string;
  phoneFormatted: string;
}

export interface ApplicationPersonAddress {
  id: string;
  address1: string;
  address2: string | null;
  city: string;
  state: string | null;
  postalCode: string | null;
  countryCode: string;
  addressType: string;
  isVerified: boolean | null;
}

export interface EligibilityCheck {
  type: string;
  status: string;
}

export interface ApplicationEligibilityCriterion {
  complete: boolean;
  expired?: boolean | null;
  expiresAt?: string | null;
}

export interface AssociateApplicationEligibility {
  eligible: boolean;
  blockingReasons: string[];
  backgroundCheck: ApplicationEligibilityCriterion;
  playingAbilityTest: ApplicationEligibilityCriterion;
  qualifyingLevel: ApplicationEligibilityCriterion;
  dues: {
    complete: boolean;
    balanceCents: number;
  };
  age: {
    complete: boolean;
    dateOfBirth?: string | null;
  };
}

export interface AffiliateApplicationEligibility {
  eligible: boolean;
  blockingReasons: string[];
  backgroundCheck: ApplicationEligibilityCriterion;
  qualifyingLevel: ApplicationEligibilityCriterion;
  age: {
    complete: boolean;
    dateOfBirth?: string | null;
  };
}

export interface MemberApplicationEligibility {
  eligible: boolean;
  blockingReasons: string[];
  experienceCredits: {
    total: number;
    threshold: number;
    complete: boolean;
  };
}

export interface MembershipApplicationChangelog {
  id: number;
  description: string;
  modifiedBy: string;
  timestamp: string;
}

export interface MembershipApplicationReviewer {
  id: string;
  name: string;
  email: string | null;
}

export interface MembershipApplicationUpdatedFields {
  informalName: boolean;
  gender: boolean;
  profilePhoto: boolean;
  homePhone: boolean;
  mobilePhone: boolean;
  homeAddress: boolean;
  directoryOptOut: boolean;
}

export interface ApplicationEmployment {
  id: string;
  title: string | null;
  formattedTitle: string | null;
  jobClassification: string | null;
  tourCode: string | null;
  startedOn: string | null;
  endedOn: string | null;
  isPrimary: boolean;
  workingLocation: "ON_SITE" | "REMOTE" | null;
  employmentKind: "MEMBER" | "NON_MEMBER";
  affiliationSection: {
    id: string;
    name: string;
  } | null;
  facility: {
    id: string;
    name: string;
    addresses: {
      id: string;
      address1: string;
      city: string;
      state: string | null;
      postalCode: string | null;
      addressType: string;
    }[];
  };
}

export interface MembershipApplicationDetail {
  id: string;
  applicationType: string;
  status: string;
  reviewerTaskId: string | null;
  reviewer: MembershipApplicationReviewer | null;
  firstName: string | null;
  lastName: string | null;
  informalName: string | null;
  dateOfBirth: string | null;
  gender: string | null;
  hasSsn: boolean;
  email: string | null;
  mobilePhone: string | null;
  homePhone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  spousePartnerName: string | null;
  directoryOptOut: boolean | null;
  memberCardFulfillment: boolean | null;
  spouseCardFulfillment: boolean | null;
  agreedToTerms: boolean | null;
  beneficiaryDesignationAttested: boolean | null;
  hasCriminalHistory: boolean | null;
  lastAmateurEvent: boolean | null;
  lastAmateurEventDate: string | null;
  eligibilityStatuses: EligibilityCheck[];
  experienceCredits: ExperienceCredits | null;
  associateApplicationEligibility: AssociateApplicationEligibility | null;
  affiliateApplicationEligibility: AffiliateApplicationEligibility | null;
  memberApplicationEligibility: MemberApplicationEligibility | null;
  profilePhotoUrl: string | null;
  createdAt: string;
  submittedAt: string | null;
  updatedAt: string;
  changelogs: MembershipApplicationChangelog[];
  registrationOrder?: ApplicationOrderSummary | null;
  upcomingDuesOrder?: ApplicationOrderSummary | null;
  updatedFields: MembershipApplicationUpdatedFields;
  criminalHistoryDocuments: ApplicationDocument[] | null;
  educationDetails: ApplicationEducationDetails | null;
  educationDocuments: ApplicationDocument[] | null;
  jobDescriptionDocuments: ApplicationDocument[] | null;
  currentEmploymentDetails: ApplicationEmploymentDetails | null;
  previousEmploymentDetails: ApplicationEmploymentDetails | null;
  backofficeEmployments: ApplicationEmployment[] | null;
  primaryBeneficiaryInfo: ApplicationBeneficiary[] | null;
  contingentBeneficiaryInfo: ApplicationBeneficiary[] | null;
  person: {
    pgaId: string;
    displayName: string;
    suffixName: string | null;
    profilePhoto: string | null;
    email: string | null;
    membershipStatus: string | null;
    primarySection: {
      id: string;
      name: string;
    } | null;
    phoneNumbers: ApplicationPersonPhoneNumber[] | null;
    contactAddressId: string | null;
    addresses: ApplicationPersonAddress[] | null;
    latestProgram: {
      status: string;
      programType: {
        name: string;
      } | null;
      band: {
        terminatedReason?: string | null;
        classification: {
          name: string;
          description: string | null;
        } | null;
      } | null;
    } | null;
    age: number | null;
    middleName: string | null;
    preferredLastName: string | null;
    ssn: {
      last4?: string;
      full?: string;
    } | null;
    militaryService: {
      id: string;
      status: string;
    } | null;
    primarySectionAffiliation: {
      id: string;
      section: {
        id: string;
        name: string;
      };
    } | null;
    prospectProgram?: {
      backgroundCheck?: BackgroundCheck | null;
      qualifyingLevel?: QualifyingLevel | null;
      playingAbilityTestStatus?: ApplicationPlayingAbilityTestStatus | null;
    } | null;
    pgmProgressStatus?: ApplicationPgmProgressStatus | null;
  };
}

export interface MembershipApplicationQueryResponse {
  membershipApplication: MembershipApplicationDetail | null;
}

export interface MembershipApplicationQueryVariables {
  id: string;
}

export const APPLICATION_EMPLOYMENT_DETAILS_FRAGMENT = gql`
  fragment ApplicationEmploymentDetailsFields on MembershipEmploymentDetails {
    facilityId
    employer
    sectionName
    jobTitle
    jobClassification
    startDate
    endDate
    country
    supervisorName
    jobDescription
    workingLocation
    affiliationSection {
      id
      name
    }
  }
`;

export const APPLICATION_ORDER_SUMMARY_FRAGMENT = gql`
  fragment ApplicationOrderSummaryFields on CommerceOrder {
    id
    updatedAt
    status
    balance
    paymentStatus
    description
    person {
      displayName
      pgaId
    }
  }
`;

export const MEMBERSHIP_APPLICATION_QUERY: TypedDocumentNode<
  MembershipApplicationQueryResponse,
  MembershipApplicationQueryVariables
> = gql`
  query MembershipApplication($id: ID!) {
    membershipApplication(id: $id) {
      id
      applicationType
      status
      reviewerTaskId
      reviewer {
        id
        name
        email
      }
      firstName
      lastName
      informalName
      dateOfBirth
      gender
      hasSsn
      email
      mobilePhone
      homePhone
      address1
      address2
      city
      state
      postalCode
      country
      spousePartnerName
      directoryOptOut
      agreedToTerms
      beneficiaryDesignationAttested
      hasCriminalHistory
      lastAmateurEvent
      lastAmateurEventDate
      eligibilityStatuses {
        type
        status
      }
      associateApplicationEligibility {
        eligible
        blockingReasons
        backgroundCheck {
          complete
          expired
          expiresAt
        }
        playingAbilityTest {
          complete
          expired
          expiresAt
        }
        qualifyingLevel {
          complete
          expired
          expiresAt
        }
        dues {
          complete
          balanceCents
        }
        age {
          complete
          dateOfBirth
        }
      }
      affiliateApplicationEligibility {
        eligible
        blockingReasons
        backgroundCheck {
          complete
          expired
          expiresAt
        }
        qualifyingLevel {
          complete
          expired
          expiresAt
        }
        age {
          complete
          dateOfBirth
        }
      }
      memberApplicationEligibility {
        eligible
        blockingReasons
        experienceCredits {
          total
          threshold
          complete
        }
      }
      experienceCredits {
        total
        threshold
        complete
        employmentCredits
        educationCredits
        sources {
          origin
          sourceId
          creditType
          credits
          description
          startedOn
          endedOn
        }
      }
      profilePhotoUrl
      createdAt
      submittedAt
      updatedAt
      changelogs {
        id
        description
        modifiedBy
        timestamp
      }
      registrationOrder {
        ...ApplicationOrderSummaryFields
      }
      upcomingDuesOrder {
        ...ApplicationOrderSummaryFields
      }
      updatedFields {
        informalName
        gender
        profilePhoto
        homePhone
        mobilePhone
        homeAddress
        directoryOptOut
      }
      criminalHistoryDocuments {
        id
        filename
        url
        createdAt
      }
      educationDetails {
        schoolName
        graduationMonth
        graduationYear
        highestLevelOfEducation
      }
      educationDocuments {
        id
        filename
        url
        createdAt
      }
      jobDescriptionDocuments {
        id
        filename
        url
        createdAt
      }
      currentEmploymentDetails {
        ...ApplicationEmploymentDetailsFields
      }
      previousEmploymentDetails {
        ...ApplicationEmploymentDetailsFields
      }
      backofficeEmployments {
        id
        title
        formattedTitle
        jobClassification
        tourCode
        startedOn
        endedOn
        isPrimary
        workingLocation
        employmentKind
        affiliationSection {
          id
          name
        }
        facility {
          id
          name
          addresses {
            id
            address1
            city
            state
            postalCode
            addressType
          }
        }
      }
      primaryBeneficiaryInfo {
        fullName
        relationship
        dateOfBirth
        ssnLast4
        percent
      }
      contingentBeneficiaryInfo {
        fullName
        relationship
        dateOfBirth
        ssnLast4
        percent
      }
      person {
        pgaId
        displayName
        suffixName
        profilePhoto
        email
        membershipStatus
        primarySection {
          id
          name
        }
        phoneNumbers {
          ...PhoneNumberFragment
        }
        contactAddressId
        addresses {
          ...AddressFragment
        }
        latestProgram {
          status
          programType {
            name
          }
          band {
            terminatedReason
            classification {
              name
              description
            }
          }
        }
        age
        middleName
        preferredLastName
        ssn {
          last4
          full
        }
        militaryService {
          id
          status
        }
        primarySectionAffiliation {
          id
          section {
            id
            name
          }
        }
        prospectProgram {
          backgroundCheck {
            status
            startDate: screenedAt
            expirationDate: expiredAt
          }
          qualifyingLevel {
            status
            completed
            levelCode
            expirationDate: expiredAt
            endDate: completedAt
          }
          playingAbilityTestStatus {
            status
            completed
            expired
            startDate
            expirationDate
            activeSuspension {
              startDate
              endDate
            }
          }
        }
        pgmProgressStatus {
          currentLevelKey
          currentLevelCode
          curriculumVersion
          mostRecentCompletedLevelKey
          programExpirationDate
          levels {
            key
            name
            status
            numeral
            startDate
            expirationDate
          }
        }
      }
    }
  }
  ${APPLICATION_EMPLOYMENT_DETAILS_FRAGMENT}
  ${PHONE_NUMBER_FRAGMENT}
  ${ADDRESS_FRAGMENT}
  ${APPLICATION_ORDER_SUMMARY_FRAGMENT}
`;

export interface MembershipApplicationApprovalEmailPreview {
  subject: string;
  html: string;
}

export interface MembershipApplicationApprovalEmailPreviewQueryResponse {
  membershipApplicationApprovalEmailPreview: MembershipApplicationApprovalEmailPreview;
}

export interface MembershipApplicationApprovalEmailPreviewQueryVariables {
  applicationId: string;
  messageToApplicant?: string | null;
}

export const MEMBERSHIP_APPLICATION_APPROVAL_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  MembershipApplicationApprovalEmailPreviewQueryResponse,
  MembershipApplicationApprovalEmailPreviewQueryVariables
> = gql`
  query MembershipApplicationApprovalEmailPreview($applicationId: ID!, $messageToApplicant: String) {
    membershipApplicationApprovalEmailPreview(applicationId: $applicationId, messageToApplicant: $messageToApplicant) {
      subject
      html
    }
  }
`;

export interface MembershipApplicationDenialEmailPreview {
  subject: string;
  html: string;
}

export interface MembershipApplicationDenialEmailPreviewQueryResponse {
  membershipApplicationDenialEmailPreview: MembershipApplicationDenialEmailPreview;
}

export interface MembershipApplicationDenialEmailPreviewQueryVariables {
  applicationId: string;
  messageToApplicant?: string | null;
}

export const MEMBERSHIP_APPLICATION_DENIAL_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  MembershipApplicationDenialEmailPreviewQueryResponse,
  MembershipApplicationDenialEmailPreviewQueryVariables
> = gql`
  query MembershipApplicationDenialEmailPreview($applicationId: ID!, $messageToApplicant: String) {
    membershipApplicationDenialEmailPreview(applicationId: $applicationId, messageToApplicant: $messageToApplicant) {
      subject
      html
    }
  }
`;
